import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { apiAction } from "@client/lib/actions";
import { getMultiselectValue } from "@client/lib/settings";
import { TargetSelector, getSettingValueSelector } from "@client/selectors";
import { type AppState, type BaseAppState, type EntityRef, Status } from "@client/types";
import { toOptionItems } from "@common/lib/api";
import { isUndefined } from "lodash";
import { type Dispatch } from "redux";
import { initAction } from "../actions";
import api from "./api";
import { localStateSelector, makeForumOptionsSelector } from "./selectors";
import { type Forums } from "./types";

export enum Actions {
  InitBegin = "MEMBER_FORUM_INIT_BEGIN",
  InitFailure = "MEMBER_FORUM_INIT_FAILURE",
  InitSuccess = "MEMBER_FORUM_INIT_SUCCESS",

  LoadPostPageBegin = "MEMBER_FORUM_LOAD_POST_PAGE_BEGIN",
  LoadPostPageFailure = "MEMBER_FORUM_LOAD_POST_PAGE_FAILURE",
  LoadPostPageSuccess = "MEMBER_FORUM_LOAD_POST_PAGE_SUCCESS",

  AddPostBegin = "MEMBER_FORUM_ADD_POST_BEGIN",
  AddPostFailure = "MEMBER_FORUM_ADD_POST_FAILURE",
  AddPostSuccess = "MEMBER_FORUM_ADD_POST_SUCCESS",

  LoadPostCommentsPageBegin = "MEMBER_FORUM_LOAD_POST_COMMENTS_PAGE_BEGIN",
  LoadPostCommentsPageFailure = "MEMBER_FORUM_LOAD_POST_COMMENTS_PAGE_FAILURE",
  LoadPostCommentsPageSuccess = "MEMBER_FORUM_LOAD_POST_COMMENTS_PAGE_SUCCESS",

  AddPostCommentBegin = "MEMBER_FORUM_ADD_POST_COMMENT_BEGIN",
  AddPostCommentFailure = "MEMBER_FORUM_ADD_POST_COMMENT_FAILURE",
  AddPostCommentSuccess = "MEMBER_FORUM_ADD_POST_COMMENT_SUCCESS",

  RemovePostBegin = "MEMBER_FORUM_REMOVE_POST_BEGIN",
  RemovePostFailure = "MEMBER_FORUM_REMOVE_POST_FAILURE",
  RemovePostSuccess = "MEMBER_FORUM_REMOVE_POST_SUCCESS",

  RemovePostCommentBegin = "MEMBER_FORUM_REMOVE_POST_COMMENT_BEGIN",
  RemovePostCommentFailure = "MEMBER_FORUM_REMOVE_POST_COMMENT_FAILURE",
  RemovePostCommentSuccess = "MEMBER_FORUM_REMOVE_POST_COMMENT_SUCCESS",

  SetActiveAction = "MEMBER_FORUM_SET_ACTIVE_ACTION",
  SetShowingPostId = "MEMBER_FORUM_SET_SHOWING_POST_ID",

  UpdatePostBegin = "MEMBER_FORUM_UPDATE_POST_BEGIN",
  UpdatePostFailure = "MEMBER_FORUM_UPDATE_POST_FAILURE",
  UpdatePostSuccess = "MEMBER_FORUM_UPDATE_POST_SUCCESS",

  UpdatePostCommentBegin = "MEMBER_FORUM_UPDATE_POST_COMMENT_BEGIN",
  UpdatePostCommentFailure = "MEMBER_FORUM_UPDATE_POST_COMMENT_FAILURE",
  UpdatePostCommentSuccess = "MEMBER_FORUM_UPDATE_POST_COMMENT_SUCCESS"
}

export type InitBeginActionData = EntityRef & {
  selectedForumIds: string[];
  page: number;
  size: number;
  search: string | null;
};
export type InitSuccessActionData = {
  forumOptions: Forums.Api.ForumOptionItem[];
  posts: Forums.Api.Post[];
};
export const _init = initAction<AppState, InitBeginActionData, Forums.Api.InitResponse, InitSuccessActionData>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: Actions.InitSuccess
  },
  action: async (state: AppState, dispatch: any, params: InitBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.init(apiInfo, params);
  },
  mapResponse: (state: AppState, resp: Forums.Api.InitResponse) => {
    return {
      ...resp,
      forumOptions: toOptionItems<any>(resp.forumOptions) as Forums.Api.ForumOptionItem[]
    };
  }
});

export const init = (args: EntityRef) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);

  if (localState.status === Status.None) {
    const forumsSelector = getSettingValueSelector(TargetSelector.Entity, "forum", undefined, getMultiselectValue);
    const forums = forumsSelector(state, args);
    dispatch(
      _init({
        ...args,
        page: 1,
        size: localState.size,
        search: null,
        selectedForumIds: forums!
      })
    );
  }
};

export type LoadPostPageBeginActionData = EntityRef & {
  availableForumIds: string[] | null;
  page: number;
  size: number;
  forumId: string | null;
  search: string | null;
};
export type LoadPostPageSuccessActionData = Forums.Api.GetPostPageResponse;
export const _loadPostPage = apiAction<BaseAppState, LoadPostPageBeginActionData, LoadPostPageSuccessActionData>({
  types: {
    begin: Actions.LoadPostPageBegin,
    failure: Actions.LoadPostPageFailure,
    success: Actions.LoadPostPageSuccess
  },
  action: async (state: BaseAppState, dispatch: any, params: LoadPostPageBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.getPostPage(apiInfo, params);
  }
});

export type LoadPostPageArgs = EntityRef & {
  forumId?: string | null;
  page?: number;
  size?: number;
};
export const loadPostPage = (args: LoadPostPageArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);

  if (localState.status !== Status.Loading) {
    const forumOptions = makeForumOptionsSelector()(state, args);

    dispatch(
      _loadPostPage({
        ...args,
        availableForumIds: forumOptions.map((x) => x.value),
        forumId: isUndefined(args.forumId) ? localState.forumId : args.forumId,
        page: args.page ?? localState.page,
        size: args.size ?? localState.size,
        search: localState.search
      })
    );
  }
};

export type SearchPostsArgs = EntityRef & {
  search: string;
};
export const searchPosts = (args: SearchPostsArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);

  if (localState.status !== Status.Loading) {
    const forumOptions = makeForumOptionsSelector()(state, args);

    dispatch(
      _loadPostPage({
        ...args,
        availableForumIds: forumOptions.map((x) => x.value),
        forumId: localState.forumId,
        page: 1,
        size: localState.size,
        search: args.search
      })
    );
  }
};

export type LoadPostCommentsPageBeginActionData = EntityRef & {
  page: number;
  ticketId: string;
};
export type LoadPostCommentsPageSuccessActionData = Forums.Api.GetPostCommentsResponse;
export const _loadPostCommentsPage = apiAction<
  BaseAppState,
  LoadPostCommentsPageBeginActionData,
  LoadPostCommentsPageSuccessActionData
>({
  types: {
    begin: Actions.LoadPostCommentsPageBegin,
    failure: Actions.LoadPostCommentsPageFailure,
    success: Actions.LoadPostCommentsPageSuccess
  },
  action: async (state: BaseAppState, dispatch: any, params: LoadPostCommentsPageBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.getPostCommentPages(apiInfo, { ticketId: params.ticketId, page: params.page, size: 1000 });
  }
});

export type LoadPostCommentsPageArgs = EntityRef & {
  page: number;
  ticketId: string;
};
export const loadPostCommentsPage =
  (args: LoadPostCommentsPageArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const localState = localStateSelector(state, args);
    if (localState.status !== Status.Loading) {
      dispatch(_loadPostCommentsPage(args));
    }
  };

export type AddPostBeginActionData = EntityRef & {
  data: Forums.View.AddEditPost;
};
export type AddPostSuccessActionData = Forums.Api.AddUpdatePostResponse;
export const _addPost = apiAction<BaseAppState, AddPostBeginActionData, AddPostSuccessActionData>({
  types: {
    begin: Actions.AddPostBegin,
    failure: Actions.AddPostFailure,
    success: [
      Actions.AddPostSuccess,
      ({ dispatch, params, res }) =>
        dispatch(setShowingPostId({ id: params.id, source: params.source, ticketId: res.ticketId }))
    ]
  },
  action: async (state: BaseAppState, dispatch: any, params: AddPostBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);

    return api.addPost(apiInfo, params.data);
  }
});

export const addPost =
  (ref: EntityRef, data: Forums.View.AddEditPost) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    dispatch(_addPost({ ...ref, data }));
  };

export type RemovePostBeginActionData = EntityRef & {
  ticketId: string;
};
export type RemovePostSuccessActionData = Forums.Api.RemovePostResponse;
export const removePost = apiAction<BaseAppState, RemovePostBeginActionData, RemovePostSuccessActionData>({
  types: {
    begin: Actions.RemovePostBegin,
    failure: Actions.RemovePostFailure,
    success: [
      ({ dispatch, params }) => {
        dispatch(setActiveAction(params, null));
        dispatch(setShowingPostId({ id: params.id, source: params.source, ticketId: undefined }));
      },
      Actions.RemovePostSuccess
    ]
  },
  action: async (state: BaseAppState, dispatch: any, params: RemovePostBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);

    return api.removePost(apiInfo, params.ticketId);
  }
});

export type UpdatePostBeginActionData = EntityRef & {
  data: Forums.Api.UpdatePostRequest;
};
export type UpdatePostSuccessActionData = Forums.Api.UpdatePostResponse;
export const updatePost = apiAction<BaseAppState, UpdatePostBeginActionData, UpdatePostSuccessActionData>({
  types: {
    begin: Actions.UpdatePostBegin,
    failure: Actions.UpdatePostFailure,
    success: [Actions.UpdatePostSuccess, ({ dispatch, params }) => dispatch(setActiveAction(params, null))]
  },
  action: async (state: BaseAppState, dispatch: any, params: UpdatePostBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);

    return api.updatePost(apiInfo, params.data);
  }
});

export type AddPostCommentBeginActionData = EntityRef & {
  ticketId: string;
  data: Forums.View.AddEditPostComment;
};
export type AddPostCommmentSuccessActionData = Forums.Api.AddUpdatePostCommentResponse;
export const _addPostComment = apiAction<BaseAppState, AddPostCommentBeginActionData, AddPostCommmentSuccessActionData>(
  {
    types: {
      begin: Actions.AddPostCommentBegin,
      failure: Actions.AddPostCommentFailure,
      success: [({ dispatch, params }) => dispatch(setActiveAction(params, null)), Actions.AddPostCommentSuccess]
    },
    action: async (state: BaseAppState, dispatch: any, params: AddPostCommentBeginActionData) => {
      const apiInfo = makeModuleApiInfoSelector()(state, params);

      return api.addPostComment(apiInfo, params.ticketId, params.data);
    }
  }
);

export const addPostComment =
  (ref: EntityRef, data: Forums.View.AddEditPostComment) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const localState = localStateSelector(getState(), ref);

    dispatch(_addPostComment({ ...ref, ticketId: localState.showingPostId!, data }));
  };

export type RemovePostCommentBeginActionData = EntityRef & {
  ticketId: string;
  ticketCommentId: string;
};
export type RemovePostCommentSuccessActionData = Forums.Api.RemovePostCommentResponse;
export const removePostComment = apiAction<
  BaseAppState,
  RemovePostCommentBeginActionData,
  RemovePostCommentSuccessActionData
>({
  types: {
    begin: Actions.RemovePostCommentBegin,
    failure: Actions.RemovePostCommentFailure,
    success: [Actions.RemovePostCommentSuccess, ({ dispatch, params }) => dispatch(setActiveAction(params, null))]
  },
  action: async (state: BaseAppState, dispatch: any, params: RemovePostCommentBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);

    return api.removePostComment(apiInfo, params.ticketId, params.ticketCommentId);
  }
});

export type UpdatePostCommentBeginActionData = EntityRef & {
  data: Forums.Api.UpdatePostCommentRequest;
};
export type UpdatePostCommentSuccessActionData = Forums.Api.UpdatePostCommentResponse;
export const updatePostComment = apiAction<
  BaseAppState,
  UpdatePostCommentBeginActionData,
  UpdatePostCommentSuccessActionData
>({
  types: {
    begin: Actions.UpdatePostCommentBegin,
    failure: Actions.UpdatePostCommentFailure,
    success: [Actions.UpdatePostCommentSuccess, ({ dispatch, params }) => dispatch(setActiveAction(params, null))]
  },
  action: async (state: BaseAppState, dispatch: any, params: UpdatePostCommentBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);

    return api.updatePostComment(apiInfo, params.data.ticketId, params.data);
  }
});

export type SetShowingPostIdActionData = EntityRef & {
  ticketId: string | null | undefined;
};
export const setShowingPostId =
  (args: SetShowingPostIdActionData) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    dispatch({
      type: Actions.SetShowingPostId,
      data: <SetShowingPostIdActionData>args
    });

    if (args.ticketId) {
      dispatch(loadPostCommentsPage({ ...args, ticketId: args.ticketId, page: 1 }));
      dispatch(setActiveAction(args, { target: "post", targetId: args.ticketId, type: "reply" }));
    }
  };

export type SetActiveActionActionData = EntityRef & {
  action: Forums.View.Action;
};
export const setActiveAction =
  (ref: EntityRef, action: Forums.View.Action | null) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const localState = localStateSelector(getState(), ref);

    dispatch({
      type: Actions.SetActiveAction,
      data: {
        ...ref,
        action: action ?? { target: "post", type: "reply", targetId: localState.showingPostId! }
      }
    });
  };

export type ConfirmActiveActionActionData = EntityRef & {
  data?: any;
};
export const confirmActiveAction =
  (args: ConfirmActiveActionActionData) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const localState = localStateSelector(getState(), args);
    if (!localState.activeAction) return;

    switch (localState.activeAction.type) {
      case "delete": {
        if (localState.activeAction.target === "post") {
          dispatch(removePost({ id: args.id, source: args.source, ticketId: localState.activeAction.targetId }));
        } else if (localState.activeAction.target === "comment") {
          dispatch(
            removePostComment({
              id: args.id,
              source: args.source,
              ticketId: localState.showingPostId!,
              ticketCommentId: localState.activeAction.targetId
            })
          );
        }
        break;
      }
      case "reply": {
        const data = args.data as Forums.View.AddEditPostComment;
        dispatch(
          addPostComment(args, {
            ...data,
            ticketId: localState.activeAction.targetId,
            parentTicketCommentId:
              localState.activeAction.target === "comment" ? localState.activeAction.targetId : undefined
          })
        );
        break;
      }
      case "update": {
        if (localState.activeAction.target === "post") {
          const data = args.data as Forums.View.AddEditPost;
          dispatch(
            updatePost({
              id: args.id,
              source: args.source,
              data: { ...data, ticketId: localState.activeAction.targetId }
            })
          );
        } else if (localState.activeAction.target === "comment") {
          const data = args.data as Forums.View.AddEditPostComment;
          dispatch(
            updatePostComment({
              id: args.id,
              source: args.source,
              data: { ...data, ticketCommentId: localState.activeAction.targetId, ticketId: localState.showingPostId! }
            })
          );
        }
        break;
      }
    }
  };

export const backToPosts = (ref: EntityRef) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  dispatch(setShowingPostId({ ...ref, ticketId: undefined }));

  dispatch(loadPostPage(ref));
};
